import { Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type {
  SessionProblemResponseDto,
  SessionReadingResponseDto,
  SessionTransitionResponseDto,
} from '@evimo/types/api/session/session.response.dto';
import { useActivityWords } from '~/app/components/session/hooks/useActivityWords';

export function useTransitionColumns(): ColumnsType<SessionTransitionResponseDto> {
  const words = useActivityWords();
  return [
    {
      title: words.t('columns.from'),
      dataIndex: 'startedAt',
      render: (value: string) => words.clock(value),
    },
    {
      title: words.t('columns.until'),
      dataIndex: 'endedAt',
      render: (value: string | null) =>
        value ? words.clock(value) : words.t('still'),
    },
    {
      title: words.t('columns.howLong'),
      dataIndex: 'durationMinutes',
      render: (value: number, row) =>
        row.endedAt ? words.minutes(value) : words.t('ongoing'),
    },
    {
      title: words.t('columns.state'),
      dataIndex: 'status',
      render: (value: string) => words.status(value),
    },
    {
      title: words.t('columns.note'),
      dataIndex: 'note',
    },
  ];
}

export function useProblemColumns(): ColumnsType<SessionProblemResponseDto> {
  const words = useActivityWords();
  return [
    {
      title: words.t('columns.whatHappened'),
      dataIndex: 'faultType',
      render: (value: string, row) => (
        <div>
          <div>{words.problem(value)}</div>
          {row.technicalCode ? (
            <div
              style={{
                fontSize: 12,
                color: 'var(--ant-color-text-description, #8c8c8c)',
              }}
            >
              {words.t('technicalCode', { code: row.technicalCode })}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      title: words.t('columns.howSerious'),
      dataIndex: 'severity',
      render: (value: string) => words.severity(value),
    },
    {
      title: words.t('columns.whichPart'),
      dataIndex: 'component',
      render: (value: string) => words.name(value),
    },
    {
      title: words.t('columns.from'),
      dataIndex: 'startedAt',
      render: (value: string) => words.clock(value),
    },
    {
      title: words.t('columns.until'),
      dataIndex: 'endedAt',
      render: (value: string | null) =>
        value ? words.clock(value) : words.t('still'),
    },
    {
      title: words.t('columns.now'),
      dataIndex: 'isResolved',
      render: (value: boolean) => (
        <Tag color={value ? 'success' : 'warning'}>
          {value ? words.t('fixed') : words.t('open')}
        </Tag>
      ),
    },
  ];
}

export function useReadingColumns(): ColumnsType<SessionReadingResponseDto> {
  const words = useActivityWords();
  return [
    {
      title: words.t('columns.when'),
      dataIndex: 'recordedAt',
      render: (value: string) => words.clock(value),
    },
    {
      title: words.t('columns.reading'),
      dataIndex: 'parameter',
      render: (value: string) => words.readingName(value),
    },
    {
      title: words.t('columns.value'),
      key: 'value',
      render: (_, row) => words.readingValue(row.value, row.unit),
    },
    {
      title: words.t('columns.where'),
      dataIndex: 'location',
      render: (value: string) => words.name(value),
    },
  ];
}
